import {
  ActionLink,
  Autocomplete,
  Banner,
  Box,
  Button,
  Chip,
  CommonDialog,
  ConfirmCancelDialog,
  DjangoTableWithPagination,
  ExternalLink,
  InternalButtonLink,
  ListItem,
  LoadingButton,
  LoadingDialog,
  OrderedList,
  Select,
  SingleButtonDialog,
  Spacer,
  Spinner,
  Tab,
  TabPanel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Text,
  TextField,
  Title,
  Tooltip,
  useDialogState,
  useDjangoPaginatedQuery,
  useToast,
  Checkbox,
} from '@color/continuum';
import { ApiClient, ApiError } from '@color/lib';
import { useOnQueryError } from '@color/lib/QueryUtils';
import CheckIcon from '@mui/icons-material/Check';
import { useMutation, useQuery } from '@tanstack/react-query';
import { capitalize, round, truncate, isEqual, omit } from 'lodash-es';
import React, { useCallback, useState, useEffect } from 'react';

import { IgvBrowserTooltip } from '../../IGV/IgvBrowserTooltip';
import { CNV_CANDIDATES_CREATE_PATH } from '../paths';

import { BrowsingFilters } from './BrowsingFilter';
import { ColumnVisibilityControl, useColumnVisibility } from './ColumnVisibilityControl';
import { SubmitReviewDialogue } from './SubmitReviewDialogue';
import { Candidate, ReviewOnHoldData, ReviewDecision, ReviewDetails } from './types';
import { formatPercentage } from './utils';

const COLUMNS = Object.fromEntries(
  Object.entries({
    reviewDecision: { name: 'Review Decision', widthRatio: 0.01 },
    reviewNote: { name: 'Review Note', widthRatio: 0.2 },
    nearbyLongIndels: { name: 'Nearby Long Indels' },
    sequenceName: { name: 'Sequence Name' },
    limitations: { name: 'Sequencing Limitations' },
    images: { name: 'Images' },
    algorithm: { name: 'Algorithm' },
    start: { name: 'Start' },
    end: { name: 'End' },
    insertedSeq: { name: 'Inserted Sequence' },
    gene: { name: 'Gene' },
    class: { name: 'Class' },
    medianRatio: { name: 'Median Ratio' },
    copyNumber: { name: 'Copy Number' },
    size: { name: 'Size' },
    splitReads: { name: 'Split Reads' },
    pairedEndReads: { name: 'Paired-end Reads' },
    usdInfo: { name: 'Usd Info' },
    exonNomenclature: { name: 'Exon Nomenclature' },
    similarCallCounts: { name: 'Similar Call Counts' },
    tags: { name: 'Tags' },
    orderedTests: { name: 'Ordered Tests' },
    startLabels: { name: 'Start Labels' },
    endLabels: { name: 'End Labels' },
  }).map(([id, attrs]) => [id, { id, ...attrs, isSortable: false }])
);

const REVIEW_DECISIONS: ReviewDecision[] = [
  ReviewDecision.UNKNOWN,
  ReviewDecision.PRESENT,
  ReviewDecision.ABSENT,
  ReviewDecision.MAYBE,
  ReviewDecision.DUPLICATE,
];

const REVIEW_DECISION_OPTIONS = REVIEW_DECISIONS.map((d) => ({ label: capitalize(d), value: d }));

const REVIEW_NOTE_COMPLETIONS = [
  'Clear ratios',
  'Weak ratios',
  'Common spurious',
  'Did not review',
  'Split read dupe',
];

const MAX_INSERTED_SEQ_LENGTH = 20;

const InsertedSeq = ({ seq }: { seq?: string }) => {
  const { dialogIsOpen, openDialog, closeDialog } = useDialogState();
  const text = seq || '';

  if (text.length <= MAX_INSERTED_SEQ_LENGTH) {
    return <Text>{text}</Text>;
  }

  return (
    <Text>
      <ActionLink onClick={openDialog}>
        {truncate(text, { length: MAX_INSERTED_SEQ_LENGTH, omission: '...' })}
      </ActionLink>
      <CommonDialog title="Inserted Sequence" dialogIsOpen={dialogIsOpen} onClose={closeDialog}>
        <Box sx={{ paddingX: 3, paddingBottom: 3 }}>
          <Text
            sx={{
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
            }}
          >
            {text}
          </Text>
        </Box>
      </CommonDialog>
    </Text>
  );
};

const CustomTableCell = ({ children, sx, ...props }: { children: React.ReactNode; sx?: any }) => {
  return (
    <TableCell
      {...props}
      sx={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'background.darkBg',
        ...sx,
      }}
    >
      {children}
    </TableCell>
  );
};

export const BaseCandidatesBlock = (props: {
  candidates: Candidate[];
  hiddenColumns: Set<string>;
  renderReviewDecision: (candidate: Candidate) => React.ReactElement;
  renderReviewNote: (candidate: Candidate) => React.ReactElement;
  renderAdditionalButtons?: () => React.ReactElement;
  renderFooter?: () => React.ReactElement | undefined;
}) => {
  const {
    candidates,
    hiddenColumns,
    renderReviewDecision,
    renderReviewNote,
    renderAdditionalButtons,
    renderFooter,
  } = props;

  const first = candidates[0];
  const { batch_label, barcode } = first.sample_sequence_run;
  const { kit_barcode } = first.sample_sequence_run.sample || { kit_barcode: 'none' };
  const { chrom } = first;
  const hasNearbyLongIndels = candidates.some((c) => c.nearby_long_indels.length);
  const hasLimitations = candidates.some((c) => c.sample_sequence_run.limitations.length);

  return (
    <TableRow key={[batch_label, kit_barcode, chrom].join('-')}>
      <TableCell sx={{ padding: 1 }}>
        <Box
          sx={{
            padding: 1,
            backgroundColor: 'background.darkBg',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
              }}
            >
              {[
                ['Batch name', batch_label],
                ['kit barcode', kit_barcode],
                ['Sequence name', barcode],
                ['chrom', chrom],
              ].map(([name, val]) => {
                return (
                  <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Text component="span">{name}:</Text>
                    <Chip size="small" color="info" label={val} />
                  </Box>
                );
              })}
            </Box>
            {renderAdditionalButtons?.()}
          </Box>
          {hasNearbyLongIndels && (
            <Text variant="caption" color="red">
              Has Nearby Long Indels
            </Text>
          )}
          {hasLimitations && (
            <Text variant="caption" color="red">
              Has Sequencing Limitations
            </Text>
          )}
        </Box>
        <Table
          columns={Object.entries(COLUMNS)
            .filter(([id]) => !hiddenColumns.has(id))
            .map(([, attrs]) => attrs)}
          tableOptions={{ sortDirections: {}, setSortDirections: () => {} }}
          isLoading={false}
        >
          <TableBody>
            {candidates.map((candidate: any) => {
              return (
                <TableRow
                  hover
                  key={candidate.id}
                  sx={
                    candidate.sample_sequence_run.limitations.length ||
                    candidate.nearby_long_indels.length
                      ? { outline: '3px solid pink', outlineOffset: -7 }
                      : {}
                  }
                >
                  {!hiddenColumns.has(COLUMNS.reviewDecision.id) && (
                    <CustomTableCell key={COLUMNS.reviewDecision.id} sx={{ minWidth: 200 }}>
                      {renderReviewDecision(candidate)}
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.reviewNote.id) && (
                    <CustomTableCell key={COLUMNS.reviewNote.id} sx={{ minWidth: 300 }}>
                      {renderReviewNote(candidate)}
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.nearbyLongIndels.id) && (
                    <CustomTableCell key={COLUMNS.nearbyLongIndels.id} sx={{ minWidth: 140 }}>
                      <OrderedList>
                        {candidate.nearby_long_indels.map(([pos, id]: [string, string]) => (
                          <ListItem key={id}>
                            <Tooltip title={pos} placement="right" arrow>
                              <ExternalLink
                                to={`/clinical/v2/variant_classifications/${id}`}
                                sx={{ color: 'red' }}
                              >
                                {truncate(pos, { length: 8, omission: '...' })}
                              </ExternalLink>
                            </Tooltip>
                          </ListItem>
                        ))}
                      </OrderedList>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.sequenceName.id) && (
                    <CustomTableCell key={COLUMNS.sequenceName.id}>
                      <Box sx={{ typography: 'body1' }}>
                        <IgvBrowserTooltip
                          igvLink={candidate.igv_link}
                          ssrId={candidate.sample_sequence_run.id}
                          header={candidate.sample_sequence_run.barcode}
                        >
                          <ExternalLink to={candidate.igv_link}>
                            {truncate(candidate.sample_sequence_run.barcode, {
                              length: 8,
                              omission: '...',
                            })}
                          </ExternalLink>
                        </IgvBrowserTooltip>
                        <br />
                        Controls:{' '}
                        {candidate.control_igv_links.map(
                          ([name, url, controlSsrId]: [string, string, number]) => (
                            <React.Fragment key={`${name}-${url}`}>
                              <IgvBrowserTooltip igvLink={url} ssrId={controlSsrId}>
                                <ExternalLink to={url}>{name}</ExternalLink>
                              </IgvBrowserTooltip>{' '}
                            </React.Fragment>
                          )
                        )}
                        <br />
                        Breakpoints:{' '}
                        {candidate.breakpoint_igv_links.map(([name, url]: [string, string]) => (
                          <React.Fragment key={`${name}-${url}`}>
                            <IgvBrowserTooltip
                              igvLink={url}
                              ssrId={candidate.sample_sequence_run.id}
                            >
                              <ExternalLink to={url}>{name}</ExternalLink>
                            </IgvBrowserTooltip>{' '}
                          </React.Fragment>
                        ))}
                      </Box>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.limitations.id) && (
                    <CustomTableCell key={COLUMNS.limitations.id}>
                      <Text sx={{ color: 'red' }}>
                        {candidate.sample_sequence_run.limitations.join(', ')}
                      </Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.images.id) && (
                    <CustomTableCell key={COLUMNS.images.id} sx={{ minWidth: 160 }}>
                      <Text>
                        {candidate.gene_ratio && (
                          <>
                            <ExternalLink to={candidate.gene_ratio}>GeneRatio</ExternalLink>
                            <br />
                          </>
                        )}
                        {!candidate.sample_ratio_links && candidate.sample_ratio && (
                          <>
                            <ExternalLink to={candidate.sample_ratio}>SampleRatio</ExternalLink>
                            <br />
                          </>
                        )}
                        {candidate.sample_ratio_links &&
                          candidate.sample_sequence_run.test_types.map((test_type: string) => {
                            return (
                              <>
                                <ExternalLink
                                  key={test_type}
                                  to={candidate.sample_ratio_links[test_type]}
                                >
                                  SampleRatio ({test_type})
                                </ExternalLink>
                                <br />
                              </>
                            );
                          })}
                        {candidate.pca_plot && (
                          <>
                            <ExternalLink to={candidate.pca_plot}>PCAPlot</ExternalLink>
                            <br />
                          </>
                        )}
                        {candidate.raw_pca_plot && (
                          <ExternalLink to={candidate.raw_pca_plot}>RawPCAPlot</ExternalLink>
                        )}
                      </Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.algorithm.id) && (
                    <CustomTableCell key={COLUMNS.algorithm.id}>
                      <Text>{candidate.algorithm}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.start.id) && (
                    <CustomTableCell key={COLUMNS.start.id}>
                      <Text>{candidate.start}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.end.id) && (
                    <CustomTableCell key={COLUMNS.end.id}>
                      <Text>{candidate.end}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.insertedSeq.id) && (
                    <CustomTableCell key={COLUMNS.insertedSeq.id}>
                      <InsertedSeq seq={candidate.inserted_seq} />
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.gene.id) && (
                    <CustomTableCell key={COLUMNS.gene.id}>
                      <Text>{candidate.region_name}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.class.id) && (
                    <CustomTableCell key={COLUMNS.class.id}>
                      <Text>
                        {candidate.cnv_class === 'INSERTION' ? (
                          <>
                            <ExternalLink to={candidate.insertion_assembly_link}>
                              {candidate.cnv_class}
                            </ExternalLink>
                            <br />
                            {candidate.insertion_model_features.insertion_desc}
                          </>
                        ) : (
                          <>{candidate.cnv_class}</>
                        )}
                      </Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.medianRatio.id) && (
                    <CustomTableCell key={COLUMNS.medianRatio.id}>
                      <Text>
                        {candidate.median_ratio !== null && round(candidate.median_ratio, 3)}
                      </Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.copyNumber.id) && (
                    <CustomTableCell key={COLUMNS.copyNumber.id}>
                      <Text>{candidate.copy_number}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.size.id) && (
                    <CustomTableCell key={COLUMNS.size.id}>
                      <Text>{candidate.size}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.splitReads.id) && (
                    <CustomTableCell key={COLUMNS.splitReads.id}>
                      <Text>{candidate.splitread_count}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.pairedEndReads.id) && (
                    <CustomTableCell key={COLUMNS.pairedEndReads.id}>
                      {candidate.lumpy_su && (
                        <Box sx={{ typography: 'body1' }}>
                          {candidate.lumpy_pe}
                          <br />
                          qual={round(candidate.lumpy_qual, 0)}
                          <br />
                          <IgvBrowserTooltip
                            igvLink={candidate.range_igv_link}
                            ssrId={candidate.sample_sequence_run.id}
                          >
                            <ExternalLink to={candidate.range_igv_link}>IGV</ExternalLink>
                          </IgvBrowserTooltip>
                        </Box>
                      )}
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.usdInfo.id) && (
                    <CustomTableCell key={COLUMNS.usdInfo.id}>
                      {candidate.usd_confidence && (
                        <Text>
                          {candidate.usd_confidence}
                          <br />
                          AF={formatPercentage(candidate.usd_allele_fraction)}%
                          <br />
                          Reads={candidate.usd_total_unique_reads}
                        </Text>
                      )}
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.exonNomenclature.id) && (
                    <CustomTableCell key={COLUMNS.exonNomenclature.id}>
                      <Text>{candidate.filtered_nomenclatures}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.similarCallCounts.id) && (
                    <CustomTableCell key={COLUMNS.similarCallCounts.id} sx={{ minWidth: 120 }}>
                      <Text>
                        {candidate.similar_call_counts.map(([name, count]: [string, number]) => {
                          return (
                            <>
                              {capitalize(name)}: {count}
                              <br />
                            </>
                          );
                        })}
                      </Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.tags.id) && (
                    <CustomTableCell key={COLUMNS.tags.id}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {candidate.tags.map((tag: string) => {
                          return <Chip key={tag} size="small" color="info" label={tag} />;
                        })}
                      </Box>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.orderedTests.id) && (
                    <CustomTableCell key={COLUMNS.orderedTests.id} sx={{ minWidth: 140 }}>
                      <Text>{candidate.sample_sequence_run.test_types.join(', ')}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.startLabels.id) && (
                    <CustomTableCell key={COLUMNS.startLabels.id}>
                      <Text>{candidate.clean_start_label}</Text>
                    </CustomTableCell>
                  )}
                  {!hiddenColumns.has(COLUMNS.endLabels.id) && (
                    <CustomTableCell key={COLUMNS.endLabels.id}>
                      <Text>{candidate.clean_end_label}</Text>
                    </CustomTableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {renderFooter?.()}
      </TableCell>
    </TableRow>
  );
};

const BrowsingCandidatesBlock = ({
  candidates,
  hiddenColumns,
  onServerStateChange = () => {},
}: {
  candidates: Candidate[];
  hiddenColumns: Set<string>;
  onServerStateChange: () => void;
}) => {
  const mutation = useMutation({
    mutationFn: async (id: number) => {
      return ApiClient.post('/api/v1/cnv_candidates/reset_review_decision', { json: { id } });
    },
  });

  const { showToast } = useToast();

  const {
    dialogIsOpen: confirmDialogIsOpen,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialogState();

  return (
    <BaseCandidatesBlock
      candidates={candidates}
      hiddenColumns={hiddenColumns}
      renderReviewDecision={({ id, review_decision, why_cant_reset_review_decision }) => {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {why_cant_reset_review_decision ? (
              <Tooltip
                title={`Can't reset because: ${why_cant_reset_review_decision}`}
                placement="right"
                arrow
              >
                <Chip label={review_decision} />
              </Tooltip>
            ) : (
              <>
                <Chip label={review_decision} />
                <LoadingButton
                  size="small"
                  variant="color-secondary"
                  loadingIndicatorIsVisible={mutation.isPending}
                  onClick={() => {
                    openConfirmDialog();
                  }}
                >
                  Reset
                </LoadingButton>
                <ConfirmCancelDialog
                  title="Reset decision"
                  cancelButtonText="Cancel"
                  confirmButtonText="Reset"
                  dialogIsOpen={confirmDialogIsOpen}
                  onClose={closeConfirmDialog}
                  onCancel={closeConfirmDialog}
                  onAccept={() => {
                    mutation.mutate(id, {
                      onSuccess: () => {
                        showToast('Decision reset', {
                          severity: 'success',
                          variant: 'filled',
                        });
                        onServerStateChange();
                      },
                      onError: (error: ApiError) => {
                        showToast(error.message, { severity: 'error', variant: 'filled' });
                      },
                    });
                    closeConfirmDialog();
                  }}
                >
                  <Text>Are you sure you want to reset the decision?</Text>
                </ConfirmCancelDialog>
              </>
            )}
          </Box>
        );
      }}
      renderReviewNote={({ review_note }) => <Text>{review_note}</Text>}
    />
  );
};

const ReviewCandidatesBlock = ({
  candidates,
  hiddenColumns,
  onLock,
  onUnlock,
  onServerStateChange,
}: {
  candidates: Candidate[];
  hiddenColumns: Set<string>;
  onLock: () => void;
  onUnlock: () => void;
  onServerStateChange: () => void;
}) => {
  const {
    dialogIsOpen: reviewDialogIsOpen,
    openDialog: openReviewDialog,
    closeDialog: closeReviewDialog,
  } = useDialogState();

  const reviewHoldMutation = useMutation({
    mutationFn: async (data: ReviewOnHoldData) => {
      return ApiClient.post('/api/v1/cnv_candidate_blocks/set_review_hold', { json: data });
    },
  });

  const { showToast } = useToast();

  const [updates, setUpdates] = useState<Record<number, ReviewDetails>>({});
  const [canMark, setCanMark] = useState(false);

  const updateCandidate = useCallback(
    (candidate_id: number, attrs: ReviewDetails) => {
      const candidateAttrs: ReviewDetails = { ...updates[candidate_id], ...attrs };
      const newUpdates: Record<number, ReviewDetails> = {
        ...updates,
        [candidate_id]: candidateAttrs,
      };
      setUpdates(newUpdates);

      setCanMark(
        candidates
          .map(({ id }) => {
            const candidateUpdates = newUpdates[id] || {};
            return !!(
              candidateUpdates.review_decision &&
              candidateUpdates.review_decision !== 'unknown' &&
              candidateUpdates.review_note
            );
          })
          .reduce((acc, val) => acc && val)
      );
    },
    [candidates, updates]
  );

  const onReviewDecisionUpdate = useCallback(
    (candidate_id: number, new_value: ReviewDecision) => {
      updateCandidate(candidate_id, { review_decision: new_value });
    },
    [updateCandidate]
  );

  const onReviewNoteUpdate = useCallback(
    (candidate_id: number, new_value: string) => {
      updateCandidate(candidate_id, { review_note: new_value });
    },
    [updateCandidate]
  );

  const updatedAttr = useCallback(
    (candidate_id: number, attr: 'review_decision' | 'review_note', default_val: string) => {
      const candidateAttrs = updates[candidate_id];
      if (!candidateAttrs) {
        return default_val;
      }
      return candidateAttrs[attr] || default_val;
    },
    [updates]
  );

  const onHoldChange = useCallback(() => {
    onLock();
    reviewHoldMutation.mutate(
      {
        cnv_candidate_ids: candidates.map((c) => c.id),
        hold: !candidates.some((c) => c.review_on_hold),
      },
      {
        onSuccess: () => {
          onServerStateChange();
        },
        onError: (error: ApiError) => {
          showToast(error.message, { severity: 'error', variant: 'filled' });
        },
        onSettled: () => onUnlock(),
      }
    );
  }, [candidates, onLock, onUnlock, reviewHoldMutation, onServerStateChange, showToast]);

  return (
    <BaseCandidatesBlock
      candidates={candidates}
      hiddenColumns={hiddenColumns}
      renderReviewDecision={({ id, review_decision, review_on_hold }) => (
        <Select
          native
          disabled={review_on_hold}
          value={updatedAttr(id, 'review_decision', review_decision)}
          onChange={(e) => onReviewDecisionUpdate(id, e.target.value as ReviewDecision)}
        >
          {REVIEW_DECISION_OPTIONS.map(({ label, value }) => (
            <option key={label} value={value}>
              {label}
            </option>
          ))}
        </Select>
      )}
      renderReviewNote={({ id, review_note, review_on_hold }) => (
        <Autocomplete
          freeSolo
          options={REVIEW_NOTE_COMPLETIONS}
          value={updatedAttr(id, 'review_note', review_note || '')}
          onInputChange={(_, v) => onReviewNoteUpdate(id, v)}
          disableClearable
          disabled={review_on_hold}
          renderInput={(params) => (
            <TextField
              {...params}
              multiline
              disabled={review_on_hold}
              minRows={1}
              maxRows={3}
              optionalIndicatorIsEnabled={false}
              autoComplete="off"
              slotProps={{
                input: { ...params.InputProps },
                htmlInput: { ...params.inputProps },
              }}
            />
          )}
        />
      )}
      renderAdditionalButtons={() => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ width: 100 }}>
            <Checkbox
              label="hold"
              variant="outlined"
              value={candidates.some((c) => c.review_on_hold)}
              onChange={onHoldChange}
              isFullWidth
            />
          </Box>
          <Button
            disabled={!canMark}
            size="small"
            endIcon={<CheckIcon />}
            onClick={openReviewDialog}
            sx={{ width: 180, marginRight: 1 }}
          >
            Mark Reviewed
          </Button>
        </Box>
      )}
      renderFooter={() => {
        if (reviewDialogIsOpen) {
          return (
            <SubmitReviewDialogue
              candidates={candidates}
              updates={updates}
              onClose={closeReviewDialog}
              onLock={onLock}
              onUnlock={onUnlock}
              onServerStateChange={onServerStateChange}
            />
          );
        }

        return undefined;
      }}
    />
  );
};

const BrowsingPanel = ({
  hiddenColumns,
  refreshKey,
  onServerStateChange,
}: {
  hiddenColumns: Set<string>;
  refreshKey: number;
  onServerStateChange: () => void;
}) => {
  const { showToast } = useToast();

  const [filters, setFilters] = useState({});

  const browsingCandidatesResponse = useDjangoPaginatedQuery<Candidate[]>(
    '/api/v1/cnv_candidate_blocks',
    {
      initialRowsPerPage: 5,
      additionalQueryParams: filters,
    }
  );

  useOnQueryError(browsingCandidatesResponse, (error) => {
    if (error) {
      showToast(error.message, { severity: 'error', variant: 'filled' });
    }
  });

  const { data, isLoading, isFetching, paginationOptions, tableOptions, refetch } =
    browsingCandidatesResponse;

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  return (
    <TabPanel aria-labelledby="Browsing" id="tab-panel-Browse">
      <BrowsingFilters onChange={(val) => setFilters(val)} />
      <DjangoTableWithPagination
        isLoading={isLoading || isFetching}
        columns={[]}
        tableOptions={tableOptions}
        paginationOptions={paginationOptions}
        djangoResponse={data}
      >
        <TableBody>
          {data?.results?.map((candidates: Candidate[]) => (
            <BrowsingCandidatesBlock
              key={candidates[0].id}
              candidates={candidates}
              hiddenColumns={hiddenColumns}
              onServerStateChange={onServerStateChange}
            />
          ))}
        </TableBody>
      </DjangoTableWithPagination>
    </TabPanel>
  );
};

const ReviewPanel = ({
  hiddenColumns,
  refreshKey,
  onServerStateChange,
}: {
  hiddenColumns: Set<string>;
  refreshKey: number;
  onServerStateChange: () => void;
}) => {
  const { showToast } = useToast();

  const reviewCandidatesResponse = useDjangoPaginatedQuery<Candidate[]>(
    '/api/v1/cnv_candidate_blocks',
    {
      initialRowsPerPage: 5,
      additionalQueryParams: { state: 'new' },
    }
  );

  useOnQueryError(reviewCandidatesResponse, (error) => {
    if (error) {
      showToast(error.message, { severity: 'error', variant: 'filled' });
    }
  });

  const { data, isLoading, isFetching, paginationOptions, tableOptions, refetch } =
    reviewCandidatesResponse;

  const blocks = data?.results ?? [];

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  const { data: onHoldStatuses } = useQuery({
    queryKey: ['get-on-hold-statuses'],
    queryFn: () => {
      return ApiClient.get('/api/v1/cnv_candidate_blocks/on_hold_statuses', {
        params: { cnv_candidate_ids: blocks.flatMap((c) => c.map((c) => c.id)) },
      });
    },
    enabled: blocks.length > 0,
    refetchInterval: 5000,
  });

  const [locked, setLocked] = useState(false);

  if (blocks.length > 0 && onHoldStatuses) {
    const candidateIds = blocks.flatMap((block) => block.map((c) => c.id));
    if (
      isEqual(new Set(candidateIds), new Set(Object.keys(onHoldStatuses).map(Number))) &&
      !isEqual(
        Object.fromEntries(blocks.flatMap((block) => block.map((c) => [c.id, c.review_on_hold]))),
        onHoldStatuses
      )
    ) {
      refetch();
    }
  }

  return (
    <TabPanel aria-labelledby="Review" id="tab-panel-Review">
      <DjangoTableWithPagination
        isLoading={isLoading || isFetching}
        columns={[]}
        tableOptions={tableOptions}
        paginationOptions={paginationOptions}
        djangoResponse={data}
      >
        <TableBody>
          {blocks.map((candidates: Candidate[]) => (
            <ReviewCandidatesBlock
              key={`${candidates[0].id}-${candidates[0].review_on_hold}`}
              candidates={candidates}
              hiddenColumns={hiddenColumns}
              onLock={() => setLocked(true)}
              onUnlock={() => setLocked(false)}
              onServerStateChange={onServerStateChange}
            />
          ))}
        </TableBody>
      </DjangoTableWithPagination>
      {locked && <LoadingDialog message="Submitting..." />}
    </TabPanel>
  );
};

const GENE_DIALOG_INPUTS = [
  ['Batch Name', 'batch_id'],
  ['Sequence Name', 'sample_id'],
  ['Gene', 'gene'],
];

interface ViewGeneRequestData {
  batch_id: string;
  sample_id: string;
  gene: string;
}

const GENE_DIALOG_INPUT_NAMES = GENE_DIALOG_INPUTS.map((e) => e[1]);

const ViewGene = () => {
  const {
    dialogIsOpen: viewGeneDialogIsOpen,
    openDialog: openViewGeneDialog,
    closeDialog: closeViewGeneDialog,
  } = useDialogState();

  const [inputsMap, setInputsMap] = useState({} as ViewGeneRequestData);

  const handleInputChange = (name: string, value: string | unknown) => {
    const newMap: ViewGeneRequestData = value
      ? { ...inputsMap, [name]: value }
      : (omit(inputsMap, name) as ViewGeneRequestData);
    setInputsMap(newMap);
  };

  const canView = GENE_DIALOG_INPUT_NAMES.map((n) => n in inputsMap).reduce(
    (acc, v) => acc && v,
    true
  );

  const mutation = useMutation({
    mutationFn: async (data: { batch_id: string; sample_id: string; gene: string }) => {
      return ApiClient.post('/api/v1/view_sample_gene', { json: data });
    },
  });

  const viewClicked = () => {
    mutation.mutate(inputsMap);
  };

  const onDialogClose = () => {
    closeViewGeneDialog();
    mutation.reset();
  };

  return (
    <>
      <Button
        size="small"
        variant="color-tertiary"
        onClick={openViewGeneDialog}
        sx={{ minWidth: '120px' }}
      >
        View Gene
      </Button>
      <SingleButtonDialog
        title="View Gene"
        buttonText="View"
        disabled={!canView}
        onClose={onDialogClose}
        dialogIsOpen={viewGeneDialogIsOpen}
        onButtonClick={viewClicked}
      >
        <Box
          sx={{
            width: '450px',
            alignContent: 'center',
            padding: 3,
          }}
        >
          {mutation.isError && (
            <Banner severity="error" variant="in-component">
              {(mutation.error as Error).message || 'An error occurred'}
            </Banner>
          )}
          {GENE_DIALOG_INPUTS.map(([label, filter]) => (
            <>
              <Spacer variant="small" />
              <Box key={filter}>
                <TextField
                  label={label}
                  name={filter}
                  optionalIndicatorIsEnabled={false}
                  onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                />
              </Box>
            </>
          ))}
          {mutation.isPending && <Spinner />}
          {mutation.isSuccess && (
            <ExternalLink to={mutation.data.link}>Click to view plot</ExternalLink>
          )}
        </Box>
      </SingleButtonDialog>
    </>
  );
};

const TABS = ['Browse', 'Review'];

export const View = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [refreshKey, setRefreshKey] = useState(0);

  const columnVisibilityState = useColumnVisibility(
    'cnv_candidates_columns_hidden',
    Object.entries(COLUMNS).map(([id, { name }]) => ({ id, name }))
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            margin: 1,
          }}
        >
          <Title variant="headline2">CNV Candidates</Title>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <InternalButtonLink
            size="small"
            variant="color-tertiary"
            to={CNV_CANDIDATES_CREATE_PATH}
            sx={{ minWidth: '180px' }}
          >
            Create Candidate
          </InternalButtonLink>
          <ViewGene />
        </Box>
      </Box>
      <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} aria-label="Tabs">
        {TABS.map((tab) => (
          <Tab label={tab} key={tab} id={`tab-${tab}`} aria-controls={`tab-panel-${tab}`} />
        ))}
      </Tabs>

      <ColumnVisibilityControl columnVisibilityState={columnVisibilityState} />

      <Box sx={{ position: 'relative', display: TABS[activeTab] === 'Browse' ? 'block' : 'none' }}>
        <BrowsingPanel
          hiddenColumns={columnVisibilityState.hiddenColumns}
          refreshKey={refreshKey}
          onServerStateChange={() => setRefreshKey((k) => k + 1)}
        />
      </Box>

      <Box sx={{ position: 'relative', display: TABS[activeTab] === 'Review' ? 'block' : 'none' }}>
        <ReviewPanel
          hiddenColumns={columnVisibilityState.hiddenColumns}
          refreshKey={refreshKey}
          onServerStateChange={() => setRefreshKey((k) => k + 1)}
        />
      </Box>
    </>
  );
};
